import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core, Decorative } from "@faire/design-tokens";
import { optimizeImageUrlHeight } from "@faire/web--source/common/images/optimizeImages";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Column, Flex, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { EmbeddedLink } from "@faire/web--source/ui/EmbeddedLink";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { ResponsiveContent } from "@faire/web--source/ui/ResponsiveContent";
import { trackBrandLeadsNewsSectionLinkedArticlesClick } from "@faire/web-api--source/events/brandLeads/click/newsSectionLinkedArticles";
import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";
import { useRetailerStore } from "@faire/retailer-visitor-shared/stores/domain/RetailerStore";
import { EUROPE_NEWS_ARTICLES_GROUP } from "@faire/retailer/views/brands/landing-page/const";
var BrandsLandingPageNewsBase = function(param) {
    var onView = param.onView;
    var viewRef = useDoActionWhenVisible(onView);
    var retailerCountry = useRetailerStore([
        "retailerCountry"
    ]).retailerCountry;
    var country = retailerCountry !== null && retailerCountry !== void 0 ? retailerCountry : "";
    // @ts-expect-error FIXME(implicitAny): https://faire.link/no-implicit-any
    var newsInfo = EUROPE_NEWS_ARTICLES_GROUP[country];
    var backgroundColor = country === "FRA" ? Core.surface.inverse : Decorative.tint.neutral;
    var mobileNewsTopGapSize = country === "FRA" ? "3x" : "4x";
    var isSSR = useIsSSR();
    if (!newsInfo) {
        return null;
    }
    return /*#__PURE__*/ _jsx(Wrapper, {
        ref: onView ? viewRef : undefined,
        $backgroundColor: backgroundColor,
        children: /*#__PURE__*/ _jsxs(NewsArticleWrapper, {
            $mobileNewsTopGapSize: mobileNewsTopGapSize,
            children: [
                /*#__PURE__*/ _jsx(ResponsiveContent, {
                    mobile: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "pageHeaderSerifRegular",
                                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                    id: "9EH12X",
                                    defaultMessage: [
                                        {
                                            type: 0,
                                            value: "In the News"
                                        }
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(Spacer, {
                                size: "3x"
                            })
                        ]
                    }),
                    desktop: null
                }),
                newsInfo.map(function(news, index) {
                    var _news_mediaArticleOverviewMobile;
                    return /*#__PURE__*/ _jsxs(NewsColumn, {
                        $index: index,
                        children: [
                            /*#__PURE__*/ _jsx(LogoRow, {
                                children: /*#__PURE__*/ _jsx(LogoImage, {
                                    src: optimizeImageUrlHeight({
                                        url: news.mediaLogoImageUrl
                                    }, isSSR, news.mediaLogoHeight),
                                    height: news.mediaLogoHeight
                                })
                            }),
                            /*#__PURE__*/ _jsx(ResponsiveContent, {
                                desktop: /*#__PURE__*/ _jsx(NewsDescription, {
                                    children: news.mediaArticleOverview
                                }),
                                mobile: /*#__PURE__*/ _jsx(NewsDescription, {
                                    children: (_news_mediaArticleOverviewMobile = news.mediaArticleOverviewMobile) !== null && _news_mediaArticleOverviewMobile !== void 0 ? _news_mediaArticleOverviewMobile : news.mediaArticleOverview
                                })
                            }),
                            /*#__PURE__*/ _jsx(Spacer, {
                                height: "1x"
                            }),
                            /*#__PURE__*/ _jsx(EmbeddedLink, {
                                linkTo: news.mediaArticleUrl,
                                target: "_blank",
                                onClick: function() {
                                    trackBrandLeadsNewsSectionLinkedArticlesClick();
                                },
                                children: /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraphSansRegular",
                                    style: {
                                        whiteSpace: "nowrap"
                                    },
                                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                        id: "XC9KIB",
                                        defaultMessage: [
                                            {
                                                type: 0,
                                                value: "Read Article"
                                            }
                                        ]
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsx(Spacer, {
                                size: "4x"
                            })
                        ]
                    }, index);
                })
            ]
        })
    });
};
export var BrandsLandingPageNews = observer(BrandsLandingPageNewsBase);
var Wrapper = styled(Column).attrs({
    align: "center",
    justify: "center"
}).withConfig({
    componentId: "sc-cf1d108e-0"
})([
    "height:404px;width:100%;",
    "{padding:",
    ";max-width:1008px;min-width:780px;}",
    "{height:fit-content;background-color:",
    ";}"
], responsiveMediaQueries.tabletAndAbove, getSpacing(0, "9x"), responsiveMediaQueries.mobile, function(props) {
    var _props_$backgroundColor;
    return (_props_$backgroundColor = props.$backgroundColor) !== null && _props_$backgroundColor !== void 0 ? _props_$backgroundColor : Decorative.tint.neutral;
});
var NewsArticleWrapper = styled(Flex).attrs({
    justify: "space-around",
    direction: {
        mobileAndAbove: "column",
        tabletAndAbove: "row"
    }
}).withConfig({
    componentId: "sc-cf1d108e-1"
})([
    "width:100%;",
    "{padding:",
    ";}"
], responsiveMediaQueries.mobile, function(props) {
    var _props_$mobileNewsTopGapSize;
    return getSpacing((_props_$mobileNewsTopGapSize = props.$mobileNewsTopGapSize) !== null && _props_$mobileNewsTopGapSize !== void 0 ? _props_$mobileNewsTopGapSize : "4x", "3x", "1x", "3x");
});
var NewsColumn = styled(Column).attrs({
    align: {
        mobileAndAbove: "flex-start",
        tabletAndAbove: "center"
    },
    justify: {
        mobileAndAbove: "flex-start",
        tabletAndAbove: "normal"
    }
}).withConfig({
    componentId: "sc-cf1d108e-2"
})([
    "",
    "{opacity:0;animation:fadeIn 0.5s ease 1 forwards;animation-delay:",
    "s;@keyframes fadeIn{to{opacity:1;}}margin:",
    ";}",
    "{width:fit-content;height:fit-content;}"
], responsiveMediaQueries.tabletAndAbove, function(props) {
    return 0.5 * props.$index;
}, getSpacing(0, "1x"), responsiveMediaQueries.mobile);
var NewsDescription = styled(Typography).withConfig({
    componentId: "sc-cf1d108e-3"
})([
    "",
    "{text-align:center;max-width:272px;min-width:272px;}",
    "{text-align:center;max-width:228px;min-width:228px;}",
    "{width:fit-content;text-align:start;align-items:center;}"
], responsiveMediaQueries.desktopAndAbove, responsiveMediaQueries.tablet, responsiveMediaQueries.mobile);
var LogoRow = styled(Row).attrs({
    justify: "center",
    align: "center"
}).withConfig({
    componentId: "sc-cf1d108e-4"
})([
    "",
    "{height:48px;}"
], responsiveMediaQueries.tabletAndAbove);
var LogoImage = styled.img.withConfig({
    componentId: "sc-cf1d108e-5"
})([
    "margin-bottom:12px;"
]);
